import { useIntl } from "react-intl";

import type { ConnectorProps, PlatformConnection } from "../../components";
import type { ConnectorWithModal } from "../../components/connector-content/interface";
import type { ConnectorPropertiesSchema } from "../schemas";
import { multiStoreConnectionsTransformer, multiStoreConnectorTransformer } from "../transformers";

import { useConnectByPost } from "./useConnectByPost";
import { useConnectorHelpClickHandlers } from "./useConnectorHelpClickHandlers";

export type ConnectorsWithLogos = (ConnectorPropsWithLogo | ConnectorWithModalAndLogo)[];
export type ConnectorPropsWithLogo = Omit<ConnectorProps, "title"> & { title: string; logoSrc?: string };
export type ConnectorWithModalAndLogo = Omit<ConnectorWithModal, "title"> & { title: string; logoSrc?: string };

export const useMultiConnectionConnectors = (
  connectorProperties: ConnectorPropertiesSchema[],
  logErrorHandler,
  shopifyPrivateApp,
  customerDomain,
  showReconnections = false
): { connectors: ConnectorsWithLogos; connections: PlatformConnection[] } => {
  const intl = useIntl();
  const [connectByPost, serverValidationErrors] = useConnectByPost(logErrorHandler);
  const connectorClickHandlers = useConnectorHelpClickHandlers();

  const connectorsRaw = multiStoreConnectorTransformer(intl, connectByPost, connectorProperties, {
    shopifyPrivateApp: shopifyPrivateApp,
    connectorClickHandlers,
    serverValidationErrors,
    customerDomain: customerDomain,
  });

  const connections = multiStoreConnectionsTransformer(
    intl,
    connectByPost,
    connectorProperties,
    {
      shopifyPrivateApp: shopifyPrivateApp,
      connectorClickHandlers,
      serverValidationErrors,
      customerDomain: customerDomain,
    },
    showReconnections
  );

  const connectors: ConnectorsWithLogos = connectorsRaw.map((c) => {
    if ("formFields" in c) {
      return {
        ...c,
        logoSrc: c.connectionLogoSrc ?? "",
        title: "title" in c && typeof c.title === "string" ? c.title : c.name,
      } as ConnectorWithModalAndLogo;
    }
    return {
      ...c,
      logoSrc: c.connectionLogoSrc ?? "",
      title: c.name,
    } as ConnectorPropsWithLogo;
  });

  return { connectors, connections };
};
